/* eslint-disable react-hooks/exhaustive-deps */
/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React, { FunctionComponent, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { globalHistory } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';

import { userCentrics } from '@savebetter/tracking-events';

import { useBankProducts } from '../context/BankProductsContext';
import { useDatasources } from '../context/DataSourceContext';
import { replaceCustomVars } from '../utils/replaceCustomVar';
interface SEOProps {
  title: string;
  description?: string;
  lang?: string;
  meta?: any[];
  disableIndexing?: boolean;
  canonical?: string;
}

export interface MetadataType {
  title?: string;
  description?: string;
}

const loadImpact = (selectorId: string, path: string, callback: () => void): void => {
  let script: HTMLScriptElement | null = document.querySelector(`#${selectorId}`);
  if (!script) {
    script = document.createElement('script');
    script.src = path;
    script.id = selectorId;
    document.body.appendChild(script);
  }

  script.onload = (): void => {
    window?.ire && window.ire('identify', { customerId: '', customerEmail: '' });
    callback && callback();
  };
};

export const SEO: FunctionComponent<React.PropsWithChildren<SEOProps>> = ({
  description = '',
  lang = 'en',
  meta = [],
  title,
  canonical,
  disableIndexing = false
}) => {
  const { contextProducts } = useBankProducts();
  const impactPath = `https://utt.impactcdn.com/A2968706-aad3-4b43-b142-3984a1d106bb1.js?${new Date().getTime()}`;
  const [impactLoaded, setImpactLoaded] = useState(false);
  const { contextDatasources } = useDatasources();
  const isMobileApp = typeof window !== 'undefined' && localStorage.getItem('mobileApp') === 'true';
  const {
    site: { siteMetadata, pathPrefix }
  } = useStaticQuery(
    graphql`
      query {
        site {
          pathPrefix
          siteMetadata {
            defaultTitle
            titleTemplate
          }
        }
      }
    `
  );

  useEffect(() => {
    userCentrics.initializeTracking();
  }, []);

  useEffect(() => {
    loadImpact('impact', impactPath, () => {
      !impactLoaded && setImpactLoaded(true);
    });
  }, []);

  const currentUrl = globalHistory.location.href;
  const website = globalHistory.location.origin + pathPrefix + '/';

  return (
    <Helmet
      htmlAttributes={{
        lang
      }}
      title={replaceCustomVars(title, contextProducts, contextDatasources)}
      defaultTitle={replaceCustomVars(siteMetadata.defaultTitle, contextProducts)}
      titleTemplate={replaceCustomVars(siteMetadata.titleTemplate, contextProducts)}
      link={canonical ? [{ rel: 'canonical', key: canonical, href: canonical }] : []}
      meta={[
        {
          property: 'title',
          content: replaceCustomVars(title, contextProducts, contextDatasources)
        },
        {
          name: 'description',
          content: replaceCustomVars(description, contextProducts, contextDatasources)
        },
        {
          property: 'og:url',
          content: currentUrl
        },
        {
          property: 'og:site_name',
          content: website
        },
        {
          property: 'og:title',
          content: replaceCustomVars(title, contextProducts, contextDatasources)
        },
        {
          property: 'og:description',
          content: replaceCustomVars(description, contextProducts, contextDatasources)
        },
        {
          property: 'og:type',
          content: 'website'
        },
        {
          name: 'twitter:title',
          content: replaceCustomVars(title, contextProducts)
        },
        {
          name: 'twitter:description',
          content: replaceCustomVars(description, contextProducts)
        },
        {
          name: 'facebook-domain-verification',
          content: 'c79tmd3sv6c38bwfwcqh3ielkeoed1'
        }
      ]
        .concat(
          [
            {
              property: 'og:image',
              content: 'https://a.storyblok.com/f/220361/420x120/10d5642387/raisin-logo-420-x-120.png'
            },
            {
              name: 'twitter:image',
              content: 'https://a.storyblok.com/f/220361/420x120/10d5642387/raisin-logo-420-x-120.png'
            }
          ],
          meta
        )
        .concat(
          disableIndexing
            ? [{ name: 'robots', content: 'noindex max-image-preview:large' }]
            : [{ name: 'robots', content: 'max-image-preview:large' }]
        )}
      base={{ href: website }}
    >
      {/* Zendesk Widget script*/}
      {!isMobileApp && (
        <script
          defer
          id="ze-snippet"
          src="https://static.zdassets.com/ekr/snippet.js?key=d4d2151f-6268-499b-965d-e44a7da24a67"
        />
      )}
    </Helmet>
  );
};
